import { merge, orderBy, pick, set } from "lodash";

/**
 * Figured a "lookup" Object of Sections, grouped by their
 * associated NextGen Document ID, would be a fast way to
 * traverse the overall Outline to look for those groups of sections
 * that have been linked to Documents.
 */
export const buildDocumentLookup = (sections) => {
  const documentLookup = {};

  if (!sections?.length) {
    return documentLookup;
  }

  let section;
  for (section of sections) {
    const { nextgenDocumentId } = section;
    if (!nextgenDocumentId) {
      continue;
    }

    const mapId = String(nextgenDocumentId);
    const entry = documentLookup[mapId];

    if (entry) {
      const entries = orderBy(entry.concat(section), ["orderIndex"], ["asc"]);
      set(documentLookup, mapId, entries);
    } else {
      set(documentLookup, mapId, [section]);
    }
  }

  return documentLookup;
};

export const parseOutlineData = (data) => {
  if (!data) {
    return null;
  }

  const sections = data?.outlineData?.sections || [];

  return merge(data, {
    outlineData: {
      sections: sections.map(({ pageLimit, sectionStatus, ...s }) => {
        return {
          ...s,
          pageLimit: pageLimit ?? "",
          status: sectionStatus ?? null,
        };
      }),
    },
    requirements: data.requirements || [],
    outlineDocuments: data.outlineDocuments || [],
    gateState: data.gateState || null,
  });
};

export const transformSectionsForTransport = (sections) => {
  return sections.map(({ id, ...s }, orderIndex) => {
    const section = pick(s, ["complianceNotes", "level", "pageLimit", "title"]);
    const isNew = /^TEMP-/.test(id);

    return isNew ? { orderIndex, ...section } : { id, orderIndex, ...section };
  });
};

export const getNextSectionIncrement = (sections = []) => {
  const values = sections.reduce((arr, { title }) => {
    const isNewTitle = /^New(\s+)Section(\s+)([0-9]+)/.test(title);

    if (!isNewTitle) {
      return arr;
    }

    const number = title.replace(/^New(\s+)Section(\s+)/, "");
    return arr.concat(number);
  }, []);

  return Math.max(...values, 0) + 1;
};

/**
 * Merge clarification answers with last-wins semantics per questionId: a later answer
 * for a questionId already present replaces the earlier one (in place, preserving order),
 * and answers for new questionIds are appended. Shared by the appendClarificationAnswers
 * reducer and the resumeOutline action so a resubmitted (prefilled) answer after a failed
 * resume never double-counts the same question.
 */
export const mergeAnswersLastWins = (existing = [], incoming = []) => {
  const merged = existing.slice();
  incoming.forEach((answer) => {
    const idx = merged.findIndex((a) => a.questionId === answer.questionId);
    if (idx === -1) {
      merged.push(answer);
    } else {
      merged[idx] = answer;
    }
  });
  return merged;
};

/**
 * Best-effort JSON.parse for a possibly-null/malformed string: degrades to null
 * rather than throwing. Used for gate payloads that carry a stringified block —
 * both the SSE path (useProposalOutline's handleGateReady) and the persisted
 * `gateState.block` a page reload gets back from the GET (gate views' fallback
 * to selectGateState when selectClarification is empty).
 */
export const safeJsonParse = (raw) => {
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch (error) {
    console.error("Failed to parse gate block JSON:", error);
    return null;
  }
};

export const createEmptySection = (titleNumber = 1, level = 1) => {
  return {
    id: `TEMP-${Date.now()}`,
    isNew: true,
    complianceNotes: "",
    level,
    pageLimit: null,
    title: `New Section ${titleNumber}`,
  };
};
